import { Alert, Modal } from 'antd';
import type { PostTypeSummaryDto, VehicleTypeSummaryDto } from '@pams/shared';
import { RequirementPreview } from '@/components/RequirementPreview';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  PUBLISH — a deliberate, confirmed action that says what it does.
 *
 *  An AntD `Modal` rather than `Modal.confirm`, because the body has structure: a
 *  warning, sometimes a second notice about the default, and the preview.
 *
 *  ⚠️ THE SAME `RequirementPreview` COMPONENT RENDERS HERE. A confirmation that restates
 *  the change in different markup is a second chance to be wrong; one component with
 *  three containers cannot disagree with itself.
 *
 *  ⚠️ AND IT IS NOT `danger`. `MergePlaceModal` and the deactivate confirms are danger
 *  because they destroy or absorb a record. Publishing destroys nothing — it is
 *  IRREVERSIBLE, which is a different property, and colouring irreversibility as
 *  destruction is how `danger` stops meaning "this loses data". The recovery from a
 *  mistaken publish is "create version n+1", which is a normal Tuesday — which is also
 *  why there is no type-the-name confirmation.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const PublishPolicyModal = ({
  open,
  policyName,
  versionNumber,
  becomesDefault,
  previousDefaultName,
  postTypeIds,
  postTypes,
  vehicleTypeIds,
  vehicleTypes,
  commandRoles,
  error,
  publishing,
  onClose,
  onConfirm,
}: {
  open: boolean;
  policyName: string;
  versionNumber: number;
  becomesDefault: boolean;
  previousDefaultName?: string;
  postTypeIds: readonly string[];
  postTypes: ReadonlyMap<string, PostTypeSummaryDto>;
  vehicleTypeIds: readonly string[];
  vehicleTypes: ReadonlyMap<string, VehicleTypeSummaryDto>;
  commandRoles: readonly { label: string; isRequired: boolean }[];
  error?: string;
  publishing: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const empty = postTypeIds.length === 0;

  return (
    <Modal
      open={open}
      width={640}
      title={`Publish version ${String(versionNumber)} of “${policyName}”?`}
      okText={`Publish version ${String(versionNumber)}`}
      okButtonProps={{ type: 'primary', disabled: empty, loading: publishing }}
      onCancel={onClose}
      onOk={onConfirm}
      destroyOnHidden
    >
      <div className="flex flex-col gap-3">
        <Alert
          type="warning"
          showIcon
          message={`Version ${String(versionNumber)} becomes permanent when you publish it.`}
          description="A published version can never be edited. Any later change creates the next version. Arrangements already raised keep pointing at the version they were made from, so nothing already ordered changes."
        />

        {becomesDefault && (
          <Alert
            type="info"
            showIcon
            message={
              previousDefaultName
                ? `“${policyName}” replaces “${previousDefaultName}” as the default policy for new arrangements.`
                : `“${policyName}” becomes the default policy for new arrangements.`
            }
          />
        )}

        {empty && (
          <Alert
            type="error"
            showIcon
            message="This version has no post columns."
            description="A column set with no columns is not one. Add at least one post type before publishing."
          />
        )}

        {/* A rejected publish is shown HERE, not as a toast — a toast disappears while
            the modal is still covering the thing that has to be fixed. */}
        {error && <Alert type="error" showIcon message={error} data-testid="publish-error" />}

        <RequirementPreview
          postTypeIds={postTypeIds}
          postTypes={postTypes}
          vehicleTypeIds={vehicleTypeIds}
          vehicleTypes={vehicleTypes}
          commandRoles={commandRoles}
        />
      </div>
    </Modal>
  );
};
